import React, { useState } from 'react';
import { Modal, Button, TextInput, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { UuidString } from '~src/utils/UUID';
import { Group } from '~/src/types/groups';
import { peep_client } from '~src/api/peep';
import ProfileImageUploader from './ProfileImageUploader';

interface GroupSettingsModalProps {
  opened: boolean;
  onClose: () => void;
  group: Group;
}

type FormState =
  | { status: 'editing'; displayName: string }
  | { status: 'submitting'; displayName: string }
  | {
      status: 'error';
      displayName: string;
      error: string;
    };

export default function GroupSettingsModal({ opened, onClose, group }: GroupSettingsModalProps): JSX.Element {
  const [state, setState] = useState<FormState>({
    status: 'editing',
    displayName: group.display_name,
  });

  const handleImageUploaded = (uploadId: UuidString): void => {
    notifications.show({
      title: 'Group Image Updated',
      message: 'Group image has been updated successfully',
      color: 'green',
    });
    onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    if (state.status !== 'editing') {
      return;
    }

    setState({ ...state, status: 'submitting' });

    try {
      await peep_client.groups.updateGroupSettings({
        group_id: group.id,
        display_name: state.displayName !== group.display_name ? state.displayName : undefined,
      });

      notifications.show({
        title: 'Group Updated',
        message: 'Group settings have been updated successfully',
        color: 'green',
      });

      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update group settings';
      setState({
        status: 'error',
        displayName: state.displayName,
        error: errorMessage,
      });
      notifications.show({
        title: 'Update Failed',
        message: errorMessage,
        color: 'red',
      });
    }
  };

  const uploadFunction = async (
    file: File,
    onProgress: (progress: number | undefined) => void,
    signal: AbortSignal,
  ): Promise<UuidString> => {
    return await peep_client.uploads.upload_group_image({ file, group_id: group.id }, onProgress, signal);
  };

  const hasChanges =
    (state.status === 'editing' || state.status === 'error') && state.displayName !== group.display_name;

  return (
    <Modal opened={opened} onClose={onClose} title="Group Settings" centered>
      <Stack gap="md">
        <ProfileImageUploader
          currentImageUrl={group.group_image}
          seedId={group.id.toString()}
          onImageUploaded={handleImageUploaded}
          uploadFunction={uploadFunction}
        />

        <TextInput
          label="Group Name"
          description="This is what the group will show up as to all users"
          value={state.status !== 'submitting' ? state.displayName : state.displayName}
          onChange={(e) => {
            if (state.status === 'editing' || state.status === 'error') {
              setState({ ...state, displayName: e.target.value, status: 'editing' });
            }
          }}
          disabled={state.status === 'submitting'}
        />

        <Button
          onClick={handleSubmit}
          disabled={state.status === 'submitting' || !hasChanges || state.displayName.trim() === ''}
          loading={state.status === 'submitting'}
        >
          Save Changes
        </Button>
      </Stack>
    </Modal>
  );
}
